import React, { useEffect, useState } from "react";
import { Activity, TeacherReference, Klass } from "../types";
import firebase from "firebase";
import { useLocation, useParams } from "react-router-dom";
import SubHeader from "../components/SubHeader";
import moment from "moment";
import Wrapper from "../components/Layout/Wrapper";
import styled from "styled-components";
import useAuth from "../hooks/useAuth";
import "react-markdown-editor-lite/lib/index.css";
import Button from "../components/Button";
import Teacher from "../components/Teacher";
import useTeachers from "../hooks/useTeachers";
import { Badge, Calendar, notification, Select } from "antd";
import useStudents from "../hooks/useStudents";
import useSessions from "../hooks/useSessions";
import Banner from "../components/Banner";
import { useHistory } from "react-router-dom";
import useClasses from "../hooks/useClasses";
import _ from "lodash";
import { nanoid } from "nanoid";
import useActivities from "../hooks/useActivities";

const FormGroup = styled.div`
  margin-bottom: 8px;
  label {
    margin-right: 12px;
  }
`;

const ContentPageWrapper = styled.div`
  padding-bottom: 32px;
  .sub-header {
    margin-bottom: 16px;
  }
  main {
    width: 65%;
    margin-right: 5%;
    margin-bottom: 62px;
  }
  aside {
    width: 30%;
  }
`;

const KlassPage = () => {
  const { isAdmin, accountDetails } = useAuth();
  const { classId } = useParams<{ classId: string }>();
  const urlDate = new URLSearchParams(useLocation().search).get("date");
  const [calendarSelectedDay, setCalendarSelectedDay] = useState<
    moment.Moment | undefined
  >(urlDate ? moment(Number(urlDate)) : undefined);

  const [newTeacher, setNewTeacher] = useState<TeacherReference>({
    teacherId: "",
    role: "Teacher",
  });
  const [filteredCourse, setFilteredCourse] = useState<string>("");
  const [sessionToAdd, setSessionToAdd] = useState<string>("");
  const [activityToAdd, setActivityToAdd] = useState<string>("");
  const [newStudents, setNewStudents] = useState<string[]>([]);
  const history = useHistory();

  const [activeClass, setActiveClass] = useState<Klass>();
  const [isLoading, setIsLoading] = useState(true);
  const { data: teacherData } = useTeachers();
  const {
    data: studentData,
    addStudentsToClass,
    removeStudentToClass,
  } = useStudents();
  const { addSessionToClass, addActivityToClass } = useClasses();
  const { data: sessionData } = useSessions();
  const { data: activityData } = useActivities();

  // Click calendar to add - see sessions on day

  useEffect(() => {
    setIsLoading(true);
    firebase
      .firestore()
      .collection("classes")
      .doc(classId)
      .get()
      .then((snapshot) => {
        const data = snapshot.data() as Klass;
        return data;
      })
      .then(setActiveClass)
      .then(() => setIsLoading(false));
  }, [classId]);

  const onItemUpdate = async (variables: Partial<Activity>) => {
    await firebase
      .firestore()
      .collection("classes")
      .doc(classId)
      .update({
        ...variables,
      })
      .then(() => {
        notification.success({ message: "Saved!" });
      });

    return variables;
  };

  const onRemoveTeacher = (oldTeacherId?: string) => {
    const nextTeachersArray = (activeClass?.teachers || []).filter(
      (x) => x.teacherId !== oldTeacherId
    );
    firebase.firestore().collection("classes").doc(classId).update({
      teachers: nextTeachersArray,
    });
    notification.success({ message: "Saved!" });
    if (activeClass) {
      setActiveClass({
        ...activeClass,
        teachers: nextTeachersArray,
      });
    }
  };

  const onAddTeacher = (nextTeacher?: TeacherReference) => {
    const nextTeachersArray = [
      ...(activeClass?.teachers || []),
      nextTeacher,
    ] as TeacherReference[];
    firebase.firestore().collection("classes").doc(classId).update({
      teachers: nextTeachersArray,
    });
    notification.success({ message: "Saved!" });
    if (activeClass) {
      setActiveClass({
        ...activeClass,
        teachers: nextTeachersArray,
      });
    }
  };

  if (isLoading) {
    return <div>Loading</div>;
  }
  if (!activeClass) {
    return <div>This class doesnt exist</div>;
  }

  const unassignedTeachers = (teacherData || []).filter(
    (x) =>
      !(activeClass?.teachers || []).some((t) => t.teacherId === x.teacherId)
  );

  const unassignedStudents = (studentData || []).filter(
    (x) => !(activeClass?.students || []).some((t) => t.id === x.id)
  );

  const sessionsForClass = activeClass?.sessions || [];

  const studentsForClass = (activeClass?.students || []).map((studentId) =>
    studentData?.find((student) => student.id === studentId.id)
  );

  const activitiesForClass = activeClass?.activities || [];

  const courses = _.uniq(
    sessionData?.map((session) => session.course).filter(Boolean)
  );

  const renderSelectedSessions = () => {
    if (!calendarSelectedDay) {
      return;
    }
    const sessionsOnDay = sessionsForClass.filter((x) =>
      moment(x?.startTime).isSame(calendarSelectedDay, "d")
    );
    return (
      <div className="sessions">
        <h2 style={{ marginTop: 16 }}>{calendarSelectedDay?.format("LL")}</h2>
        <h3
          style={{
            borderBottom: "4px solid #F0F0F0",
            paddingBottom: 4,
          }}
        >
          Lessons
        </h3>
        {sessionsOnDay.map((session) => (
          <Banner
            style={{ marginBottom: 16 }}
            title={`${session?.name}`}
            subtitle={`${session?.isVisible ? "ACTIVE" : "INACTIVE"} - ${moment(
              session?.startTime
            ).format("LLLL")}`}
            buttonText="View"
            onClick={() =>
              history.push(`/class/${classId}/session/${session?.id}`)
            }
            stars={session?.stars}
            menu={[
              {
                text: session.isVisible ? "Deactivate" : "Activate",
                onClick: () => {
                  const nextClass: Klass = {
                    ...activeClass,
                    sessions: (activeClass.sessions || []).map((x) =>
                      x.id === session.id
                        ? { ...x, isVisible: !x.isVisible }
                        : x
                    ),
                  };
                  setActiveClass(nextClass);
                  onItemUpdate(nextClass);
                },
              },
              {
                text: "Remove lesson",
                onClick: () => {
                  const nextClass: Klass = {
                    ...activeClass,
                    sessions: (activeClass.sessions || []).filter(
                      (x) => x.id !== session.id
                    ),
                  };
                  setActiveClass(nextClass);
                  onItemUpdate(nextClass);
                },
              },
            ]}
          />
        ))}
        {accountDetails?.isSuperuser && (
          <>
            <h5>Add lesson</h5>
            <Select
              value={filteredCourse}
              onChange={(value) => setFilteredCourse(value || "")}
            >
              <Select.Option key={"all"} value={""}>
                All courses
              </Select.Option>
              {courses.map((course) => (
                <Select.Option key={course} value={course || ""}>
                  {course}
                </Select.Option>
              ))}
            </Select>

            <Select
              value={sessionToAdd}
              onChange={(value) => setSessionToAdd(value)}
            >
              <Select.Option key={"all"} value={""}>
                -- Select a lesson --
              </Select.Option>
              {(sessionData || [])
                .filter((session) =>
                  filteredCourse ? session.course === filteredCourse : true
                )
                .map((session) => (
                  <Select.Option key={session.id} value={session.id}>
                    {session.name}
                  </Select.Option>
                ))}
            </Select>

            <Button
              small
              style={{ marginLeft: 16 }}
              onClick={() => {
                const sesh = (sessionData || []).find(
                  (s) => s.id === sessionToAdd
                );
                if (sesh) {
                  addSessionToClass(
                    {
                      ...sesh,
                      id: nanoid(),
                      startTime: +calendarSelectedDay
                        .hour(17)
                        .minute(0)
                        .second(0),
                      finishTime: +calendarSelectedDay
                        .hour(18)
                        .minute(0)
                        .second(0),
                      isVisible: false,
                      videoLink: "",
                    },
                    activeClass
                  ).then(setActiveClass);
                }
              }}
            >
              Add lesson
            </Button>
          </>
        )}
        <br />
        <h3
          style={{
            marginTop: 32,
            borderBottom: "4px solid #F0F0F0",
            paddingBottom: 4,
          }}
        >
          Activities
        </h3>
        {activitiesForClass
          .filter((act) =>
            calendarSelectedDay.isSame(
              moment(act.startTime).startOf("day"),
              "day"
            )
          )
          .map((activity) => (
            <Banner
              style={{ marginBottom: 16 }}
              title={`${activity?.name}`}
              subtitle={`${activity?.isVisible ? "ACTIVE" : "INACTIVE"} - ${
                activity?.acceptString ? "STRING" : "UPLOAD"
              }`}
              buttonText="View"
              onClick={() =>
                history.push(`/class/${classId}/activity/${activity?.id}`)
              }
              stars={activity?.stars}
              menu={[
                {
                  text: activity.isVisible ? "Deactivate" : "Activate",
                  onClick: () => {
                    const nextClass: Klass = {
                      ...activeClass,
                      activities: (activeClass.activities || []).map((x) =>
                        x.id === activity.id
                          ? { ...x, isVisible: !x.isVisible }
                          : x
                      ),
                    };
                    setActiveClass(nextClass);
                    onItemUpdate(nextClass);
                  },
                },
                {
                  text: "Remove activity",
                  onClick: () => {
                    const nextClass: Klass = {
                      ...activeClass,
                      activities: (activeClass.activities || []).filter(
                        (x) => x.id !== activity.id
                      ),
                    };
                    setActiveClass(nextClass);
                    onItemUpdate(nextClass);
                  },
                },
              ]}
            />
          ))}

        {accountDetails?.isSuperuser && (
          <>
            <h5>Add activity</h5>

            <Select
              value={filteredCourse}
              onChange={(value) => setFilteredCourse(value || "")}
            >
              <Select.Option key={"all"} value={""}>
                All courses
              </Select.Option>
              {courses.map((course) => (
                <Select.Option key={course} value={course || ""}>
                  {course}
                </Select.Option>
              ))}
            </Select>
            <Select
              value={activityToAdd}
              onChange={(value) => setActivityToAdd(value)}
            >
              <Select.Option key={"all"} value={""}>
                -- Select an activity --
              </Select.Option>
              {(activityData || [])
                .filter((activ) =>
                  filteredCourse ? activ.course === filteredCourse : true
                )
                .map((activity) => (
                  <Select.Option key={activity.id} value={activity.id}>
                    {activity.name}
                  </Select.Option>
                ))}
            </Select>

            <Button
              small
              style={{ marginLeft: 16 }}
              onClick={() => {
                const activity = (activityData || []).find(
                  (a) => a.id === activityToAdd
                );
                if (activity && calendarSelectedDay) {
                  addActivityToClass(
                    {
                      ...activity,
                      id: nanoid(),
                      startTime: +calendarSelectedDay
                        .clone()
                        .hour(17)
                        .minute(0)
                        .second(0),
                      finishTime: +calendarSelectedDay
                        .clone()
                        .add(1, "week")
                        .hour(17)
                        .minute(0)
                        .second(0),
                      isVisible: false,
                    },
                    activeClass
                  ).then(setActiveClass);
                }
              }}
            >
              Add activity
            </Button>
          </>
        )}
      </div>
    );
  };

  const todaysSession = sessionsForClass.find((x) =>
    moment(x.startTime).isSame(moment(), "d")
  );

  return (
    <ContentPageWrapper>
      <SubHeader
        title={activeClass?.name}
        subtitle={`${studentsForClass.length} students - ${sessionsForClass.length} sessions - ${activitiesForClass.length} activities`}
        buttonText={!!todaysSession ? "Join todays session" : undefined}
        onClick={() => {
          if (todaysSession) {
            window.open(todaysSession?.videoLink);
          }
        }}
      />
      <Wrapper style={{ justifyContent: "space-between", width: 1280 }}>
        <main>
          {isAdmin && (
            <>
              <h3>Admin</h3>
              <FormGroup>
                <label>Name</label>
                <input
                  value={activeClass.name}
                  onChange={(e) =>
                    setActiveClass({
                      ...activeClass,
                      name: e.currentTarget.value,
                    })
                  }
                />
                <Button
                  small
                  style={{ marginLeft: 16 }}
                  onClick={() => {
                    onItemUpdate({ ...activeClass });
                  }}
                >
                  Save
                </Button>
              </FormGroup>
            </>
          )}

          {renderSelectedSessions()}
          <h3>Calendar</h3>
          <Calendar
            dateCellRender={(value) => {
              const sessionsOnDay = sessionsForClass.filter((x) =>
                moment(x?.startTime).isSame(value, "d")
              );

              const activityStartOnDay = activitiesForClass.filter((x) =>
                moment(x?.startTime).isSame(value, "d")
              );
              const activityEndOnDay = activitiesForClass.filter((x) =>
                moment(x?.finishTime).isSame(value, "d")
              );
              return (
                <div className="events">
                  {sessionsOnDay.map((item) => (
                    <div key={item?.id}>
                      <Badge
                        status={item?.isVisible ? "success" : "error"}
                        text={item?.name}
                      />
                    </div>
                  ))}
                  {activityStartOnDay.map((item) => (
                    <div key={item?.id}>
                      <Badge
                        status={item?.isVisible ? "success" : "error"}
                        text={item?.name}
                      />
                    </div>
                  ))}
                  {activityEndOnDay.map((item) => (
                    <div key={item?.id}>
                      <Badge
                        status={item?.isVisible ? "success" : "error"}
                        text={`DUE: ${item?.name}`}
                      />
                    </div>
                  ))}
                </div>
              );
            }}
            defaultValue={calendarSelectedDay}
            onSelect={setCalendarSelectedDay}
          />
        </main>
        <aside>
          <h3>Teachers</h3>
          {(activeClass?.teachers || []).map((teacher) => {
            const teacherDetail = teacherData?.find(
              (x) => x.teacherId === teacher.teacherId
            );
            return (
              <Teacher
                teacher={teacherDetail}
                subtitle={teacher.role}
                onRemove={isAdmin ? onRemoveTeacher : undefined}
              />
            );
          })}
          {isAdmin &&
            accountDetails?.isSuperuser &&
            unassignedTeachers.length > 0 && (
              <div style={{ marginTop: 32 }}>
                <Select
                  onChange={(next) =>
                    setNewTeacher({
                      role: newTeacher?.role,
                      teacherId: next as string,
                    })
                  }
                  value={newTeacher.teacherId}
                >
                  <Select.Option value="">-- Select a teacher --</Select.Option>
                  {unassignedTeachers.map((x) => (
                    <Select.Option value={x.teacherId}>{x.name}</Select.Option>
                  ))}
                </Select>
                <br />
                <Select
                  onChange={(next) =>
                    setNewTeacher({
                      teacherId: newTeacher?.teacherId,
                      role: next as string,
                    })
                  }
                  value={newTeacher.role}
                >
                  <Select.Option value="">-- Select a role --</Select.Option>
                  {["Teacher", "Assistant"].map((x) => (
                    <Select.Option value={x}>{x}</Select.Option>
                  ))}
                </Select>
                <br />
                <Button small onClick={() => onAddTeacher(newTeacher)}>
                  Add
                </Button>
              </div>
            )}

          <h3>Students</h3>
          {studentsForClass.map((student) => {
            return (
              <Teacher
                teacher={student}
                subtitle={student?.email}
                onRemove={
                  isAdmin
                    ? (studentId?: string) => {
                        if (studentId)
                          removeStudentToClass(studentId, activeClass).then(
                            setActiveClass
                          );
                      }
                    : undefined
                }
              />
            );
          })}
          {isAdmin &&
            accountDetails?.isSuperuser &&
            unassignedStudents.length > 0 && (
              <div style={{ marginTop: 32 }}>
                <Select
                  mode="multiple"
                  onChange={(next) => setNewStudents(next)}
                  value={newStudents}
                  style={{ width: "100% " }}
                  placeholder="Select students"
                >
                  {unassignedStudents.map((x) => (
                    <Select.Option value={x.id}>{x.name}</Select.Option>
                  ))}
                </Select>
                <br />
                <Button
                  style={{ marginTop: 16 }}
                  small
                  onClick={() => {
                    if (newStudents) {
                      addStudentsToClass(newStudents, activeClass).then(
                        setActiveClass
                      );
                      setNewStudents([]);
                    }
                  }}
                >
                  Add
                </Button>
              </div>
            )}
        </aside>
      </Wrapper>
    </ContentPageWrapper>
  );
};

export default KlassPage;
